import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import openaiService from '../services/openaiService';

const { FiBookOpen, FiHelpCircle, FiTrendingUp, FiVs, FiExternalLink } = FiIcons;

function AIContentAssistant() {
  const [selectedTopic, setSelectedTopic] = useState('lease-vs-buy');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiContent, setAiContent] = useState('');

  const topics = [
    {
      id: 'lease-vs-buy',
      title: 'Should I lease or buy?',
      category: 'Finance',
      content: {
        summary: 'The choice between leasing and buying depends on your driving habits, budget, and preferences.',
        pros: {
          lease: ['Lower monthly payments', 'Always drive newer cars', 'Warranty coverage', 'No maintenance worries'],
          buy: ['Build equity', 'No mileage restrictions', 'Modify as you wish', 'Long-term savings']
        },
        recommendation: 'Lease if you drive <15,000 miles/year and want newer tech. Buy if you plan to keep the car 5+ years.'
      }
    },
    {
      id: 'electric-vs-gas',
      title: 'Electric vs Gas vehicles?',
      category: 'Technology',
      content: {
        summary: 'Electric vehicles offer environmental benefits and lower operating costs, while gas vehicles provide convenience and range.',
        features: ['Lower fuel costs', 'Environmental impact', 'Charging infrastructure', 'Performance differences'],
        benefits: 'EVs save money on fuel and maintenance while reducing environmental impact.'
      }
    }
  ];

  const faqs = [
    'What warranty comes with used cars?',
    'Do you offer extended warranties?',
    'What financing options are available?',
    'Can I trade in my current vehicle?',
    'Do you provide vehicle history reports?'
  ];

  const comparisons = [
    { vehicles: 'Audi Q5 vs BMW X3', category: 'Luxury SUVs' },
    { vehicles: 'Tesla Model Y vs Genesis GV60', category: 'Electric SUVs' },
    { vehicles: 'Mercedes C-Class vs BMW 3 Series', category: 'Luxury Sedans' }
  ];

  const currentTopic = topics.find(topic => topic.id === selectedTopic);

  const generateAIContent = async (prompt) => {
    setIsGenerating(true);
    try {
      const response = await openaiService.sendMessage(prompt);
      setAiContent(response);
    } catch (error) {
      setAiContent("I'm having trouble generating content right now. Please visit shawnryder.com for more information.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleComparison = async (comparison) => {
    const prompt = `Compare ${comparison.vehicles} for customers. Include performance, features, pricing, and recommendations.`;
    await generateAIContent(prompt);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            AI Content Assistant
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get expert answers to your automotive questions, powered by AI knowledge and real-world experience.
          </p>
          <div className="mt-4">
            <a 
              href="https://shawnryder.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-700 transition-colors"
            >
              <span className="text-sm">Advanced AI Content Generation</span>
              <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
            </a>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Popular Topics</h3>
              </div>
              <div className="space-y-2">
                {topics.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() => setSelectedTopic(topic.id)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedTopic === topic.id
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="font-medium">{topic.title}</div>
                    <div className="text-sm text-gray-500">{topic.category}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <SafeIcon icon={FiHelpCircle} className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold text-gray-900">Quick FAQs</h3>
              </div>
              <div className="space-y-2">
                {faqs.map((faq, index) => (
                  <button
                    key={index}
                    onClick={() => generateAIContent(`Answer this question: ${faq}`)}
                    className="w-full text-left text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 p-2 rounded transition-colors"
                  >
                    {faq}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{currentTopic.title}</h3>
                  <p className="text-blue-600">{currentTopic.category}</p>
                </div>
              </div>

              {/* AI Generated Content */}
              {aiContent && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200"
                >
                  <h4 className="font-semibold text-blue-900 mb-3">AI Generated Analysis</h4>
                  <p className="text-blue-800 whitespace-pre-wrap">{aiContent}</p>
                  <div className="mt-3 text-xs text-blue-600">
                    Generated by Shawn Ryder Digital AI • Visit{' '}
                    <a href="https://shawnryder.com" target="_blank" rel="noopener noreferrer" className="underline">
                      shawnryder.com
                    </a>
                  </div>
                </motion.div>
              )}

              {isGenerating && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">AI is generating content...</span>
                </div>
              )}

              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">{currentTopic.content.summary}</p>
                
                {currentTopic.content.pros && (
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-green-50 rounded-lg p-6">
                      <h4 className="font-semibold text-green-800 mb-3">Leasing Pros</h4>
                      <ul className="space-y-2">
                        {currentTopic.content.pros.lease.map((pro, index) => (
                          <li key={index} className="flex items-center text-green-700">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                            {pro}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-6">
                      <h4 className="font-semibold text-blue-800 mb-3">Buying Pros</h4>
                      <ul className="space-y-2">
                        {currentTopic.content.pros.buy.map((pro, index) => (
                          <li key={index} className="flex items-center text-blue-700">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                            {pro}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {currentTopic.content.features && (
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Key Features</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {currentTopic.content.features.map((feature, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-blue-600 mr-2" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6">
                  <h4 className="font-semibold text-blue-900 mb-2">AI Recommendation</h4>
                  <p className="text-blue-800">
                    {currentTopic.content.recommendation || currentTopic.content.benefits}
                  </p>
                </div>
              </div>
            </div>

            {/* AI Comparison Generator */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8"
            >
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <SafeIcon icon={FiVs} className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">AI Vehicle Comparisons</h3>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  {comparisons.map((comparison, index) => (
                    <button
                      key={index}
                      onClick={() => handleComparison(comparison)}
                      disabled={isGenerating}
                      className="bg-white rounded-lg p-4 text-left hover:shadow-md transition-shadow disabled:opacity-50"
                    >
                      <h4 className="font-medium text-gray-900">{comparison.vehicles}</h4>
                      <p className="text-sm text-gray-600">{comparison.category}</p>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default AIContentAssistant;